import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { InterviewQuestions } from '@/types';
import { isGroup, isCheckpoint, findSubgroupById } from '@/utils/questionGroupHelpers';
import { GroupContainer } from '@/components/interview-questions/GroupContainer';
import {
  DiscussionGuideFormProps,
  DiscussionGuideProjectProps,
  DiscussionGuideQuestionProps,
  GroupHandlers,
} from './types';
import { CheckpointRow } from './CheckpointRow';

interface GroupModeViewProps {
  // Core structure
  structure: {
    groupStructure: InterviewQuestions;
    isDisabled: boolean;
    isAddingQuestionsDisabled: boolean;
  };
  // Drag & drop
  dragAndDrop: {
    isActiveDrag: boolean;
    activeId: string | null;
    onDragEnd: (event: DragEndEvent) => void;
    onDragStart: (event: DragStartEvent) => void;
  };
  // Handlers
  handlers: GroupHandlers & {
    onDeleteQuestion: (idToDelete: string) => void;
    isQuestionInDatabase: (questionId: string) => boolean;
    onCheckpointLabelChange?: (checkpointId: string, label: string) => void;
    onDeleteCheckpoint?: (checkpointId: string) => void;
  };
  // Form props
  formProps: DiscussionGuideFormProps;
  // Project props
  projectProps: DiscussionGuideProjectProps;
  // Question props
  questionProps: DiscussionGuideQuestionProps;
}

export function GroupModeView({
  structure,
  dragAndDrop,
  handlers,
  formProps,
  projectProps,
  questionProps,
}: GroupModeViewProps) {
  // Destructure grouped props
  const { groupStructure, isDisabled, isAddingQuestionsDisabled } = structure;
  const { isActiveDrag, activeId, onDragEnd, onDragStart } = dragAndDrop;
  const {
    onGroupNameChange,
    onGroupRandomizeChange,
    onGroupDelete,
    onAddQuestion,
    onAddSubgroup,
    onReorderQuestions,
    onReorderSubgroups,
    onMoveQuestionBetweenGroups,
    onDeleteQuestion,
    isQuestionInDatabase,
    onCheckpointLabelChange,
    onDeleteCheckpoint,
  } = handlers;
  const { register, control, setValue, clearErrors, errors, submitAttempt } = formProps;
  const { isProjectPublished, isProjectCompleted, singleProjectData } = projectProps;
  const { questions, setOpenQuestionId, openQuestionId, setFileData, fileData, currentQuestions } =
    questionProps;
  // Helper function to check if a group at a specific index has validation errors
  const getGroupErrors = (groupIndex: number) => {
    // React Hook Form's error types don't properly support nested validation errors
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const groupError = errors?.interview_questions?.children?.[groupIndex] as any;
    if (!groupError || typeof groupError !== 'object') {
      return { hasChildrenError: false, childrenErrorMessage: undefined };
    }

    // Check if the group has children validation errors
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const hasChildrenError = 'children' in groupError && !!groupError.children;

    // Extract the error message from Yup validation for this group
    // Note: Subgroup errors are handled by the subgroups themselves
    let childrenErrorMessage: string | undefined;
    if (hasChildrenError) {
      if (typeof groupError.children === 'string') {
        childrenErrorMessage = groupError.children;
      } else if (groupError.children?.message) {
        childrenErrorMessage = groupError.children.message;
      }
    }
    return { hasChildrenError, childrenErrorMessage };
  };
  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd} onDragStart={onDragStart}>
      <SortableContext
        items={groupStructure.children
          .map((child) => {
            if (isGroup(child)) return child.id || child.name;
            if (isCheckpoint(child)) return child.id;
            return '';
          })
          .filter(Boolean)}
        strategy={verticalListSortingStrategy}
        disabled={isDisabled}
      >
        <div className="flex flex-col w-full h-full gap-5">
          {groupStructure.children?.map((child, index) => {
            if (isGroup(child)) {
              const { hasChildrenError, childrenErrorMessage } = getGroupErrors(index);
              return (
                <GroupContainer
                  key={child.id || `group-${index}`}
                  groupData={{
                    group: child as InterviewQuestions,
                    groupIndex: index,
                    groupPath: [],
                    isSubgroup: false,
                    hasChildrenError,
                    childrenErrorMessage,
                  }}
                  state={{
                    isDisabled,
                    isActiveDrag,
                    isAddingQuestionsDisabled,
                  }}
                  handlers={{
                    onGroupNameChange,
                    onGroupRandomizeChange,
                    onGroupDelete,
                    onAddQuestion,
                    onAddSubgroup,
                    onDeleteQuestion,
                    onReorderQuestions,
                    onReorderSubgroups,
                    onMoveQuestionBetweenGroups,
                    isQuestionInDatabase,
                  }}
                  formProps={{
                    register,
                    control,
                    setValue,
                    clearErrors,
                    errors,
                    submitAttempt,
                  }}
                  questionProps={{
                    questions,
                    openQuestionId,
                    setOpenQuestionId,
                    setFileData,
                    fileData,
                    currentQuestions,
                  }}
                  projectProps={{
                    isProjectPublished,
                    isProjectCompleted,
                    singleProjectData,
                  }}
                />
              );
            }
            if (isCheckpoint(child)) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const checkpointError = (errors?.interview_questions as any)?.children?.[index]
                ?.label;
              return (
                <CheckpointRow
                  key={child.id}
                  checkpoint={child}
                  isDisabled={isDisabled}
                  onLabelChange={onCheckpointLabelChange ?? (() => {})}
                  onDelete={onDeleteCheckpoint ?? (() => {})}
                  error={checkpointError}
                  clearError={() => clearErrors(`interview_questions.children[${index}].label`)}
                />
              );
            }
            return null;
          })}
        </div>
      </SortableContext>

      {/* Visual overlay showing what's being dragged */}
      <DragOverlay>
        {activeId
          ? (() => {
              // Find what's being dragged
              const allGroups = groupStructure.children.filter(isGroup);

              // Check if it's a top-level group
              const draggedGroup = allGroups.find((g) => (g.id || g.name) === activeId);
              if (draggedGroup) {
                return (
                  <div className="bg-white border-2 rounded-md shadow-lg opacity-90 min-w-[600px]">
                    <div className="flex items-center gap-3 p-4 bg-gray-50">
                      <span className="font-medium text-lg">{draggedGroup.name}</span>
                    </div>
                  </div>
                );
              }

              // Check if it's a question
              const draggedQuestion = questions.find((q) => q.id === activeId);
              if (draggedQuestion) {
                const questionIndex = questions.findIndex((q) => q.id === activeId);
                return (
                  <div className="bg-white border-2 rounded-md shadow-lg opacity-90 min-w-[600px] p-6">
                    <h3 className="font-medium">
                      {questionIndex + 1}
                      {draggedQuestion.question && <span>: {draggedQuestion.question}</span>}
                    </h3>
                  </div>
                );
              }

              // Check if it's a subgroup
              const draggedSubgroup = findSubgroupById(groupStructure, activeId);
              if (draggedSubgroup) {
                return (
                  <div className="bg-white border-2 rounded-md shadow-lg opacity-90 min-w-[400px]">
                    <div className="flex items-center gap-3 p-4 bg-gray-50">
                      <span className="font-medium text-base">{draggedSubgroup.name}</span>
                    </div>
                  </div>
                );
              }

              return null;
            })()
          : null}
      </DragOverlay>
    </DndContext>
  );
}
